import React from 'react';

import { UxContext } from '@client/context/UxContext';
import { getSocketIo } from '@client/store/globalStore/init/socketIo';
import { usePageStore } from '@client/store/pageStore/context/pageContext';
import i18n from '@client/utils/i18n';
import { Grid, ToggleButton, ToggleButtonGroup } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

import { MixerChannel, MixerSettings } from './SpectrumMixerChannelSettingsItem';

const SpectrumMixerSettings = () => {
    const { handleSnackbar } = React.useContext(UxContext)!;
    const {
        pageState: { formData },
        pageActions
    } = usePageStore();
    const [realTimeLights, setRealTimeLights] = React.useState(false);
    const realTimeLightsRef = React.useRef(realTimeLights);
    const settings = formData.settings as MixerSettings;
    const lightChannels = formData.lightChannels as MixerChannel[];

    React.useEffect(() => {
        realTimeLightsRef.current = realTimeLights;
    }, [realTimeLights]);

    React.useEffect(
        () => () => {
            if (realTimeLightsRef.current) {
                getSocketIo().emit('dmxTest:disable');
            }
        },
        []
    );

    React.useEffect(() => {
        const socket = getSocketIo();
        function onForcedDisable() {
            setRealTimeLights(false);
            handleSnackbar({ message: i18n('spectrum_mix_realtime_lights_forced_disable') as string, severity: 'warning' });
        }
        socket.on('dmxTest:forcedDisable', onForcedDisable);
        return () => {
            socket.off('dmxTest:forcedDisable', onForcedDisable);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function onChangeRealTimeLights(e: React.ChangeEvent<HTMLInputElement>) {
        setRealTimeLights(e.target.checked);
    }

    function onChangeButtonGroup(name: string, value: unknown) {
        if (value !== null) {
            pageActions.formData.updateFormItem({
                settings: {
                    ...settings,
                    [name]: value
                }
            });
        }
    }

    const realTimeControl = (enabled: boolean) => {
        const socket = getSocketIo();
        if (enabled) {
            const channels = lightChannels.map((channel) => ({
                channelId: channel.channel,
                resolution: channel.resolution,
                value: channel.intensity,
                lightChannelId: channel.id
            }));
            socket.emit('dmxTest:enable', { channels });
        } else {
            socket.emit('dmxTest:disable');
        }
    };

    React.useEffect(() => {
        realTimeControl(realTimeLights);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [realTimeLights]);

    React.useEffect(() => {
        if (realTimeLights) {
            realTimeControl(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.lightChannels]);

    if (formData.readOnly || !lightChannels.length) {
        return null;
    }

    return (
        <>
            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                <FormControlLabel
                    control={<Switch checked={realTimeLights} onChange={onChangeRealTimeLights} />}
                    label={i18n('spectrum_mix_realtime_lights')}
                />
            </Grid>
            <Grid item xs={6} md={4} sx={{ textAlign: 'center' }}>
                <ToggleButtonGroup
                    color="primary"
                    value={settings.intensityUnit}
                    exclusive
                    onChange={function (_e, value) {
                        onChangeButtonGroup('intensityUnit', value);
                    }}
                >
                    <ToggleButton value="pfd">{i18n('pfd')}</ToggleButton>
                    <ToggleButton value="irradiance">{i18n('irradiance')}</ToggleButton>
                </ToggleButtonGroup>
            </Grid>
            <Grid item xs={6} md={4} sx={{ textAlign: 'center' }}>
                <ToggleButtonGroup
                    color="primary"
                    value={settings.carouselView}
                    exclusive
                    onChange={function (_e, value) {
                        onChangeButtonGroup('carouselView', value);
                    }}
                >
                    <ToggleButton value={false}>{i18n('spectrum_mix_settings_list_view')}</ToggleButton>
                    <ToggleButton value={true}>{i18n('spectrum_mix_settings_carousel_view')}</ToggleButton>
                </ToggleButtonGroup>
            </Grid>
        </>
    );
};

export default React.memo(SpectrumMixerSettings);
